import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, Loader2 } from 'lucide-react';
import ShowcaseDashboardLayout from '../layouts/ShowcaseDashboardLayout';
import ShowcaseKreation from './showcase/ShowcaseKreation';
import ShowcaseProduktion from './showcase/ShowcaseProduktion';
import ShowcaseDistribution from './showcase/ShowcaseDistribution';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
 
export default function Showcase() {
  const { language } = useParams();
  const navigate = useNavigate();
  const { setLanguage, lang } = useLanguage();
  
  const [sessionId, setSessionId] = useState(null);
  const [activeTab, setActiveTab] = useState('kreation');
  const [loading, setLoading] = useState(true);
 
  const isDe = language !== 'en';
 
  const handleViewInProduction = () => {
    toast.info(
      isDe 
        ? 'In der Showcase-Version kann kein Content generiert werden. Schauen Sie sich die bereits fertigen Beispiele in der Produktion an!' 
        : 'Content generation is not available in Showcase mode. Check out the pre-made examples in Production!',
      { duration: 5000 }
    );
    setActiveTab('produktion');
  };
 
  useEffect(() => {
    if (!['de', 'en'].includes(language)) {
      navigate('/showcase/de');
      return;
    }
  }, [language, navigate]);
 
  useEffect(() => {
    const initShowcase = async () => {
      try {
        const storedSessionId = sessionStorage.getItem(`showcase_session_${language}`);
        
        const response = await fetch(`/api/showcase/${language}/init`, {
          headers: {
            'X-Showcase-Session-Id': storedSessionId || '',
          },
        });
 
        const data = await response.json();
        setSessionId(data.session_id);
        sessionStorage.setItem(`showcase_session_${language}`, data.session_id);
        setLoading(false);
      } catch (err) {
        console.error('Failed to init showcase:', err);
        setLoading(false);
      }
    };
 
    initShowcase();
  }, [language]);
 
  useEffect(() => {
    if (!sessionId) return;
    // Session-Info-Polling entfernt — Counter und Banner werden nicht mehr angezeigt.
  }, [sessionId, language]);
 
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-[#0A1628] to-[#0F1E32]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#00D9C8] mx-auto mb-4" />
          <p className="text-[#E0E6F2]">Loading Showcase...</p>
        </div>
      </div>
    );
  }
 
  const renderActivePage = () => {
    switch (activeTab) {
      case 'kreation':
        return (
          <ShowcaseKreation
            sessionId={sessionId}
            language={language}
            onViewInProduction={handleViewInProduction}
          />
        );
      case 'produktion':
        return (
          <ShowcaseProduktion 
            sessionId={sessionId} 
            language={language}
          />
        );
      case 'distribution':
        return <ShowcaseDistribution sessionId={sessionId} language={language} />;
      default:
        return null;
    }
  };
 
  const pageContent = (
    <div className="space-y-6 max-w-7xl">
 
      {/* Top-Right Toolbar — Language-Switcher + Showcase Mode Badge */}
      <div className="flex items-center justify-end gap-3">
        <div className="flex gap-2 bg-[#1A2D42] rounded-lg p-1" data-testid="language-switcher">
          <button
            onClick={() => { setLanguage('de'); navigate('/showcase/de'); }}
            className={`px-3 py-1 rounded transition-colors text-sm font-medium ${
              language === 'de'
                ? 'bg-[#00D9C8] text-[#0A1628]'
                : 'text-[#A0B0C8] hover:text-[#E0E6F2]'
            }`}
            data-testid="lang-switch-de"
          >
            DE
          </button>
          <button
            onClick={() => { setLanguage('en'); navigate('/showcase/en'); }}
            className={`px-3 py-1 rounded transition-colors text-sm font-medium ${
              language === 'en'
                ? 'bg-[#00D9C8] text-[#0A1628]'
                : 'text-[#A0B0C8] hover:text-[#E0E6F2]'
            }`}
            data-testid="lang-switch-en"
          >
            EN
          </button>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-500/20 border border-orange-500/50 rounded-full" data-testid="showcase-mode-badge">
          <span className="text-orange-400 text-sm font-medium">🎬 Showcase Mode</span>
        </div>
      </div>
 
      {/* Active Page Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {renderActivePage()}
      </motion.div>
    </div>
  );
 
  return (
    <ShowcaseDashboardLayout 
      language={language} 
      activeTab={activeTab} 
      setActiveTab={setActiveTab}
    >
      {pageContent}
    </ShowcaseDashboardLayout>
  );
}